import {Component, computed, effect, inject, input, InputSignal, signal, WritableSignal} from '@angular/core';
import {SharedModule} from "../../shared/shared.module";
import {ObjectCopyService} from "../object-copy.service";
import {Section, SectionsContainer} from "../../core/definitions/sections-container";
import {ThumbnailItemComponent} from "../../shared/thumbnail-item/thumbnail-item.component";
import {SearchObject} from "../../core/definitions/search-object";
import {MetaField} from "../../core/definitions/meta-field";
import {SearchParametersForOverview} from "../../core/definitions/search-parameters";
import {CmsApiService} from "../../core/cms-api.service";
import {ViewField} from "../../core/definitions/object-view";
import {MatCheckbox, MatCheckboxChange} from "@angular/material/checkbox";

@Component({
  selector: 'app-object-copy-media',
  imports: [
    SharedModule,
    ThumbnailItemComponent,
    MatCheckbox,
  ],
  templateUrl: './object-copy-media.component.html',
  styleUrl: './object-copy-media.component.scss',
})
export class ObjectCopyMediaComponent {
  readonly objectCopyService = inject(ObjectCopyService);
  private readonly cms: CmsApiService = inject(CmsApiService);

  items: SearchObject[] = [];
  isItemSelection = false;

  field: InputSignal<MetaField> = input();
  section: InputSignal<Section> = input();
  selectedMediaItems: WritableSignal<string[]> = signal([]);

  constructor() {
    effect(async () => {
      const mediaItemIds = this.itemIds();
      if (mediaItemIds.length > 0) {
        const res = await this.searchMediaItems(mediaItemIds);
        for (const mediaItem of res) {
          if (mediaItem?.thumbnail_url) {
            mediaItem.$$imageUrl = mediaItem.thumbnail_url;
          }
        }
        this.items = res;
      }
    });

    effect(() => {
      const isFieldSelected = this.isFieldSelected();
      if (this.isItemSelection) {
        this.isItemSelection = false;
        return;
      }
      this.toggleAllItems(isFieldSelected)
    });

    effect(() => {
      const selectedItemIds = this.selectedMediaItems();
      this.field().value = selectedItemIds.join(',');
    });
  }

  sectionsContainer = computed<SectionsContainer>(() =>
    this.objectCopyService.sectionsContainer());

  viewField = computed<ViewField>(() =>
    this.objectCopyService.getViewField(this.section(), this.field()));

  itemIds = computed<string[]>(() =>
    this.viewField().items.flatMap(item =>
      item.field_values.values.map(value => value.ref_id)
    )
  );

  isFieldSelected = computed<boolean>(() =>
    this.objectCopyService.getIsFieldSelected(this.field()));

  isFieldIndeterminate = computed<boolean>(() => {
    const totalItems = this.viewField().items.length;
    const selectedItems = this.selectedMediaItems().length;
    return selectedItems > 0 && selectedItems < totalItems;
  });

  onFieldToggleChange(event: MatCheckboxChange) {
    if (this.isFieldIndeterminate()) {
      event.source.checked = true;
      this.toggleAllItems(true);
      return;
    }
    this.toggleField(event.checked);
  }

  toggleField(checked: boolean): void {
    this.objectCopyService.toggleField(checked, this.field())
  }

  toggleItem(checked: boolean, item: SearchObject): void {
    const selectedItems = this.selectedMediaItems();
    this.selectedMediaItems.set(checked
      ? [...selectedItems, item.artifact_id]
      : selectedItems.filter(id => id !== item.artifact_id));
    this.isItemSelection = true;
    this.objectCopyService.toggleField(this.selectedMediaItems().length > 0, this.field())
  }

  toggleAllItems(checked: boolean): void {
    this.selectedMediaItems.set(checked ? this.itemIds() : []);
  }

  itemIsSelected(item: SearchObject): boolean {
    return this.selectedMediaItems().includes(item.artifact_id);
  }

  async searchMediaItems(mediaIds: string[]) {
    const searchParams = {
      fq: [`artifact_id:(${mediaIds.join(' ')})`],
      query: '*:*',
      rows: mediaIds.length,
      sort: 'artifact_id',
      start: 0,
      overview_simple: true,
    } as SearchParametersForOverview;
    const searchRes = await this.cms.searchWithOverview(searchParams);
    return searchRes.artifacts;
  }
}
